import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PublicHeader } from "@/components/PublicHeader";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { ArrowRight, Loader2 } from "lucide-react";

const LEAD_KEY = "sz_sayisal_lead_id";

async function ensureAnonymousLead() {
  const cached = localStorage.getItem(LEAD_KEY);
  if (cached) return cached;
  const res = await api.post("/sayisal/anonymous-lead", {});
  const id = res.data?.anonymous_lead_id;
  if (id) localStorage.setItem(LEAD_KEY, id);
  return id;
}

export default function DiagnosticStart() {
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState(null);
  const [selectedTier, setSelectedTier] = useState(null);
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/sayisal/tiers");
        setCatalog(res.data?.tiers || {});
      } catch (e) {
        toast.error(formatApiError(e));
      }
    })();
    // Prime the anonymous lead in the background.
    ensureAnonymousLead().catch(() => {});
  }, []);

  const pickTier = (tier) => {
    setSelectedTier(tier);
    setSelectedSubjects(catalog?.[tier]?.allowed_subjects || []);
  };

  const toggleSubject = (subj) => {
    if (!selectedTier) return;
    setSelectedSubjects((prev) =>
      prev.includes(subj) ? prev.filter((s) => s !== subj) : [...prev, subj],
    );
  };

  const startSession = async () => {
    if (!selectedTier || selectedSubjects.length === 0) return;
    setLoading(true);
    try {
      const lead = await ensureAnonymousLead();
      const res = await api.post("/sayisal/diagnostic-sessions", {
        tier: selectedTier,
        subject_scope: selectedSubjects,
        anonymous_lead_id: lead,
      });
      const sessionId = res.data?.id;
      if (!sessionId) throw new Error("no_session_id");
      // Fire funnel events best-effort (allowlist-only fields).
      try {
        await api.post("/sayisal/funnel-events", {
          owner_key: `lead:${lead}`,
          event_type: "diagnostic_started",
          payload: {
            tier: selectedTier,
            subject_count: selectedSubjects.length,
            session_id: sessionId,
          },
        });
        if (res.data?.status === "insufficient_package") {
          await api.post("/sayisal/funnel-events", {
            owner_key: `lead:${lead}`,
            event_type: "diagnostic_insufficient_package",
            payload: {
              tier: selectedTier,
              session_id: sessionId,
              missing_subject_count: (res.data.missing_subjects || []).length,
            },
          });
        }
      } catch {
        /* non-fatal */
      }
      navigate(`/tanilama/${sessionId}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="diagnostic-start-page">
      <PublicHeader />
      <main className="flex-1 max-w-4xl mx-auto px-5 lg:px-8 py-16 lg:py-24 w-full">
        <div className="text-xs font-semibold text-[#1E3A8A] uppercase tracking-wider">
          Ücretsiz Sayısal Tanılama
        </div>
        <h1 className="mt-3 font-display text-3xl lg:text-5xl font-bold text-zinc-900 tracking-tight">
          Sadece çözüm göstermiyoruz.
          <br />
          <span className="text-[#1E3A8A]">Neden</span> hata yaptığını anlıyoruz.
        </h1>
        <p className="mt-6 text-base sm:text-lg text-zinc-600 max-w-2xl leading-relaxed">
          SuperZeka Sayısal, hatalarının kökünde hangi ön-bilgi veya kavram eksiği olduğunu
          görmeyi hedefler. Ücretsiz tanılamayı başlat, sonucu gördüğünde kayıt olmayı seçebilirsin.
        </p>

        <section className="mt-12" data-testid="tier-selection-section">
          <h2 className="font-display text-lg font-semibold text-zinc-900">
            1. Hangi sınavı hedefliyorsun?
          </h2>
          <div className="mt-4 grid sm:grid-cols-3 gap-3">
            {catalog &&
              Object.values(catalog).map((entry) => (
                <button
                  key={entry.tier}
                  type="button"
                  onClick={() => pickTier(entry.tier)}
                  data-testid={`tier-option-${entry.tier}`}
                  className={`rounded-2xl border p-4 text-left transition-all ${
                    selectedTier === entry.tier
                      ? "border-[#1E3A8A] bg-[#1E3A8A]/5"
                      : "border-zinc-200 hover:border-zinc-300"
                  }`}
                >
                  <div className="text-sm font-semibold text-zinc-900">{entry.tier}</div>
                  <div className="mt-1 text-xs text-zinc-600 leading-snug">{entry.label}</div>
                </button>
              ))}
          </div>
        </section>

        {selectedTier && (
          <section className="mt-10" data-testid="subject-selection-section">
            <h2 className="font-display text-lg font-semibold text-zinc-900">
              2. Hangi dersleri tanılamak istiyorsun?
            </h2>
            <div className="mt-4 flex flex-wrap gap-2">
              {(catalog[selectedTier]?.allowed_subjects || []).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => toggleSubject(s)}
                  data-testid={`subject-option-${s}`}
                  className={`px-4 py-2 rounded-full border text-sm transition-all ${
                    selectedSubjects.includes(s)
                      ? "border-[#1E3A8A] bg-[#1E3A8A] text-white"
                      : "border-zinc-300 text-zinc-700 hover:border-zinc-400"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
            <p className="mt-3 text-xs text-zinc-500">
              Uyarı: Sayısal V1&apos;in içerik paketleri şu an bağımsız bir uzmanlık akışında hazırlanmakta.
              Ders aktifleşene kadar tanılama <em>hazır değil</em> durumunda kalabilir; kayıt bilgin
              alınır ve içerik yayınlanır yayınlanmaz haber verilir.
            </p>
          </section>
        )}

        <div className="mt-12 flex flex-col sm:flex-row gap-3 items-start">
          <Button
            onClick={startSession}
            disabled={!selectedTier || selectedSubjects.length === 0 || loading}
            data-testid="diagnostic-start-btn"
            className="rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white h-12 px-7 text-base"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <ArrowRight className="w-4 h-4 mr-1" />
            )}
            Tanılamayı Başlat
          </Button>
          <Link to="/" data-testid="diagnostic-back-home" className="self-center text-sm text-zinc-500 hover:text-zinc-700">
            Ana sayfaya dön
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}
